// src/lib/planMap.ts
import type { UserPlan, CardType } from "@/types/Plan";

/* -------------------------------------------------------
 * date-like → ms 変換（Timestamp / {_seconds} / Date / ISO / number）
 * ----------------------------------------------------- */
export const toMs = (v: any): number => {
  if (!v) return 0;
  // Firestore Timestamp（admin: toMillis / web: toDate）
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  // {_seconds, _nanoseconds} 形式
  if (typeof v?._seconds === "number") return v._seconds * 1000;
  // Date インスタンス
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isFinite(t) ? t : 0;
  }
  // number / string（ISO, epoch文字列）
  if (typeof v === "number") return v;
  const t = new Date(v as any).getTime();
  return Number.isFinite(t) ? t : 0;
};

/* -------------------------------------------------------
 * プラン種別ユーティリティ
 * ----------------------------------------------------- */

export const PAID_PLANS = ["discovery", "discovery_pro", "facilitator", "partner"] as const;

export const isPaidPlan = (plan: string | null | undefined) =>
  !!plan && (PAID_PLANS as readonly string[]).includes(plan);

export const isPaidOrTrial = (user: { user_plan?: string }) => {
  return isPaidPlan(user.user_plan);
};

/* -------------------------------------------------------
 * 表示用：日本語ラベル & バッジ
 * ----------------------------------------------------- */

export const PLAN_LABELS_JA: Record<string, string> = {
  free: "無料プラン",
  discovery: "ディスカバリー",
  discovery_pro: "ディスカバリー Pro",
  facilitator: "ファシリテーター",
  partner: "パートナー",
  pending: "未設定",
};

export const getPlanLabelJa = (plan?: string | null) =>
  (plan ? PLAN_LABELS_JA[plan] : "") ?? (plan ?? "");

/** UIバッジ（override中は常に🟣ファシリテーター表示） */
export const PLAN_BADGE = (userLike: any) => {
  
  const eff = getEffectivePlan(userLike);
  const text = getPlanLabelJa(eff);
  const emojiByPlan: Record<string, string> = {
    free: "🟤",
    discovery: "🟦",
    discovery_pro: "🟣",
    facilitator: "🟣",
    partner: "🟠",
  };
  return { emoji: emojiByPlan[eff] ?? "🟢", text: text || "—" };
};

/* -------------------------------------------------------
 * Operator/admin bypass
 * User whose uid matches ADMIN_USER_ID env var always receives
 * full operator capabilities, independent of billing state.
 * ----------------------------------------------------- */

export function isAdminUser(userId: string | null | undefined): boolean {
  const adminId = process.env.ADMIN_USER_ID;
  if (!adminId || !userId) return false;
  return userId === adminId;
}

/* -------------------------------------------------------
 * 実効プラン解決（override最優先）
 * ----------------------------------------------------- */

export function getEffectivePlan(user: {
  uid?: string;
  effective_plan?: string | null;
  user_plan?: string;
  previous_plan?: string;
  next_plan?: string;
  next_plan_start_date?: string | null;
  app?: { plan_override?: string | null; plan_override_expires_at?: any } | null;
  plan_override?: string | null;
  plan_override_expires_at?: any;
  badges?: { catalyst?: { level?: string | null; expires_at?: any } } | null;
  entitlements?: { full_access_until?: any } | null;
}) {
  // 0a) Server-resolved effective plan takes precedence (e.g. admin bypass resolved server-side)
  if (user.effective_plan) {
    return user.effective_plan as UserPlan;
  }

  // 0b) Operator/admin bypass — full capabilities regardless of billing (server-side only,
  //     process.env.ADMIN_USER_ID is undefined in the browser so this is a no-op client-side)
  if (isAdminUser(user.uid)) {
    return "discovery_pro" as UserPlan;
  }

  const now = Date.now();

  // 1) paid plan は最優先
  if (isPaidPlan(user.user_plan)) {
    return user.user_plan as UserPlan;
  }

  // 1.5) entitlements（期限付きフルアクセス）
  const fullUntil = toMs(user?.entitlements?.full_access_until);
  if (fullUntil && fullUntil > now) {
    return "discovery";
  }  

  // 3) 解約予約のグレース（切替日が未来なら今は有料扱い）
  if (
    user.user_plan &&
    ["discovery", "discovery_pro", "facilitator", "partner"].includes(user.user_plan) &&
    user.next_plan === "free" &&
    user.next_plan_start_date &&
    new Date(user.next_plan_start_date).getTime() > now
  ) {
    return user.user_plan as UserPlan;
  }

  // 4) fallback
  return (user.user_plan as UserPlan) ?? "free";
}

/* -------------------------------------------------------
 * プラン定義マップ（Stripe priceId もキー化）
 *  - KATARUTA Online: NEXT_PUBLIC_ONLINE_*
 *  - 現行: Discovery / Discovery Pro の PriceId を参照
 * ----------------------------------------------------- */

type PlanMapEntry = {
  user_plan: UserPlan;
  priceId?: string; // 逆参照しやすいように
  card_types_allowed: CardType[];
  can_switch_card_type: boolean;
};

export const planMap: Record<string, PlanMapEntry> = {
  // === Discovery（月額） ===
  ...(process.env.NEXT_PUBLIC_DISCOVERY_MONTH_PRICE_ID
    ? {
        [process.env.NEXT_PUBLIC_DISCOVERY_MONTH_PRICE_ID]: {
          user_plan: "discovery",
          priceId: process.env.NEXT_PUBLIC_DISCOVERY_MONTH_PRICE_ID,
          card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
          can_switch_card_type: true,
        },
      }
    : {}),

  // === Discovery Pro（月額） ===
  ...(process.env.NEXT_PUBLIC_DISCOVERY_PRO_MONTH_PRICE_ID
    ? {
        [process.env.NEXT_PUBLIC_DISCOVERY_PRO_MONTH_PRICE_ID]: {
          user_plan: "discovery_pro",
          priceId: process.env.NEXT_PUBLIC_DISCOVERY_PRO_MONTH_PRICE_ID,
          card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
          can_switch_card_type: true,
        },
      }
    : {}),

  // key 直指定（表示や fallback 用）
  discovery: {
    user_plan: "discovery",
    priceId: process.env.NEXT_PUBLIC_DISCOVERY_MONTH_PRICE_ID as string,
    card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
    can_switch_card_type: true,
  },
  discovery_pro: {
    user_plan: "discovery_pro",
    priceId: process.env.NEXT_PUBLIC_DISCOVERY_PRO_MONTH_PRICE_ID as string,
    card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
    can_switch_card_type: true,
  },

  // === Facilitator / Partner（将来枠） ===
  // ※残すならこのまま。消すならブロックごと削除でOK
  ...(process.env.NEXT_PUBLIC_FACILITATOR_MONTH_PRICE_ID
    ? {
        [process.env.NEXT_PUBLIC_FACILITATOR_MONTH_PRICE_ID]: {
          user_plan: "facilitator",
          priceId: process.env.NEXT_PUBLIC_FACILITATOR_MONTH_PRICE_ID,
          card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
          can_switch_card_type: true,
        },
      }
    : {}),
  facilitator: {
    user_plan: "facilitator",
    priceId: process.env.NEXT_PUBLIC_FACILITATOR_MONTH_PRICE_ID as string,
    card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
    can_switch_card_type: true,
  },

  ...(process.env.NEXT_PUBLIC_PARTNER_MONTH_PRICE_ID
    ? {
        [process.env.NEXT_PUBLIC_PARTNER_MONTH_PRICE_ID]: {
          user_plan: "partner",
          priceId: process.env.NEXT_PUBLIC_PARTNER_MONTH_PRICE_ID,
          card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
          can_switch_card_type: true,
        },
      }
    : {}),
  partner: {
    user_plan: "partner",
    priceId: process.env.NEXT_PUBLIC_PARTNER_MONTH_PRICE_ID as string,
    card_types_allowed: ["standard", "logical", "storytelling", "life", "emotion"],
    can_switch_card_type: true,
  },

  // === Trial / Free / Pending ===

  ...(process.env.NEXT_PUBLIC_FREE_PLAN_PRICE_ID
    ? {
        [process.env.NEXT_PUBLIC_FREE_PLAN_PRICE_ID]: {
          user_plan: "free",
          priceId: process.env.NEXT_PUBLIC_FREE_PLAN_PRICE_ID,
          card_types_allowed: ["free"],
          can_switch_card_type: false,
        },
      }
    : {}),
  free: {
    user_plan: "free",
    priceId: process.env.NEXT_PUBLIC_FREE_PLAN_PRICE_ID as string,
    card_types_allowed: ["free"],
    can_switch_card_type: false,
  },
  pending: {
    user_plan: "pending",
    card_types_allowed: [],
    can_switch_card_type: false,
  },
};

/* -------------------------------------------------------
 * エントリ解決（override考慮）
 * ----------------------------------------------------- */

export const resolvePlanEntryFromUser = (user: any) => {
  // ★ Shopify 初月ユーザーは planMap を user_plan から直接解決
  if ((user as any)?.payment_source === "shopify") {
    const effShopify = user.user_plan || "free";
    const baseSh = planMap[effShopify] ?? planMap.free;
  
    const selected =
      user?.card_type_selected && baseSh.card_types_allowed.includes(user.card_type_selected)
        ? user.card_type_selected
        : baseSh.card_types_allowed[0];
  
     return {
       user_plan: effShopify,
       card_types_allowed: [...baseSh.card_types_allowed],
       can_switch_card_type: baseSh.can_switch_card_type,
       selected,
     };
   }

  const eff = getEffectivePlan(user) as string;
  const base = planMap[eff] ?? planMap.free;

  // ルート/バックコンパチ両方を見て選択カードを拾う
  const selected: CardType | null =
    (user?.card_type_selected as CardType | undefined) ??
    (user?.badges?.card_type_selected as CardType | undefined) ??
    null;

  return {
    user_plan: eff,
    card_types_allowed: [...base.card_types_allowed],
    can_switch_card_type: base.can_switch_card_type,
    selected,
    overrideActive: false,
    overridePlan: null,
    overrideExp: null,
  };
};

/* -------------------------------------------------------
 * カードタイプ権限の総合計算
 * ----------------------------------------------------- */

export const ALL_CARD_TYPES: CardType[] = ["standard", "logical", "storytelling", "life", "emotion"];

/* -------------------------------------------------------
 * 版（Edition）→ カードタイプ マッピング
 * 物理購入した版が付与するデジタルカードタイプ
 * ----------------------------------------------------- */

export const EDITION_TO_CARD_TYPES: Record<string, CardType[]> = {
  standard:     ["standard"],
  logical:      ["logical"],
  storytelling: ["storytelling"],
  life:         ["life"],
  emotion:      ["emotion"],
};

/**
 * editions 配列から利用可能な CardType[] を返す。
 * 複数版を所持している場合はマージして返す。
 */
export function editionToCardTypes(editions: string[]): CardType[] {
  const result = new Set<CardType>();
  for (const ed of editions) {
    const types = EDITION_TO_CARD_TYPES[ed];
    if (types) types.forEach((t) => result.add(t));
  }
  return [...result];
}

/**
 * Discovery相当の機能を開けるのは「有料Discovery/Facilitator/Partner」または
 * 「期間ライセンスが有効」のみ。
 */
export function computeCardCapabilities(user: any) {
  const eff = getEffectivePlan(user) as string;

  // ✅ 実効プランが free のときのみ固定
  if (eff === "free") {
    // 🆕 版（Edition）永続アクセス：物理購入済みの版があればそのカードタイプを付与
    // （trial期限切れ後もアクセスを維持するため、free判定より前にチェック）
    const editions: string[] | undefined = user?.entitlements?.editions;
    if (Array.isArray(editions) && editions.length > 0) {
      const allowed = editionToCardTypes(editions);
      if (allowed.length > 0) {
        const rawSelected = user?.card_type_selected as CardType | undefined;
        const selected: CardType =
          (rawSelected && allowed.includes(rawSelected) && rawSelected) ||
          allowed[0];
        return { allowed, selected, can_switch: allowed.length > 1 };
      }
    }
    return {
      allowed: ["free"] as CardType[],
      selected: "free" as CardType,
      can_switch: false,
    };
  }

  // 🟡 2) Shopify 初月
  if ((user as any)?.payment_source === "shopify") {
    const base = planMap[user.user_plan ?? "free"] ?? planMap.free;
    const allowed = [...base.card_types_allowed] as CardType[];
    const raw = user?.card_type_selected as CardType | undefined;
    const selected =
      raw && allowed.includes(raw) ? raw : allowed[0];

    return {
      allowed,
      selected,
      can_switch: base.can_switch_card_type,
    };
  }

  // 🔵 3) 通常判定
  let allowed: CardType[];

  if (["discovery", "discovery_pro", "facilitator", "partner"].includes(eff)) {
    allowed = [...planMap["discovery"].card_types_allowed] as CardType[];
  } else {
    allowed =
      (planMap[eff]?.card_types_allowed as CardType[]) ??
      ["standard"];
  }
  
  const can_switch =
    ["discovery", "discovery_pro", "facilitator", "partner"].includes(eff);

  const rawSelected = user?.card_type_selected as CardType | undefined;
  const selected: CardType =
    (rawSelected && allowed.includes(rawSelected) && rawSelected) ||
    ((allowed[0] as CardType | undefined) ?? "standard");

  return { allowed, selected, can_switch };
}

// ---- override system removed (compat shim) ----
export const isOverrideActive = (_?: any): boolean => false;
export const wasOverride = (_?: any): boolean => false;
